// Support assistant API (docs/v3/ASSISTANT.md 9).
import { z } from "zod";
import { uuid } from "../schemas/common";
import type { Id } from "./common";

export const ASSISTANT_AUDIENCES = ["team", "expert"] as const;
export type AssistantAudience = (typeof ASSISTANT_AUDIENCES)[number];

export const assistantMessageBody = z.object({
  chatId: uuid.optional(),
  text: z.string().trim().min(1, "Type a question.").max(2000, "Keep the question under 2,000 characters."),
  fromPath: z.string().max(500).default("/"),
  audience: z.enum(ASSISTANT_AUDIENCES),
});
export type AssistantMessageBody = z.infer<typeof assistantMessageBody>;

export const assistantStatusQuery = z.object({ audience: z.enum(ASSISTANT_AUDIENCES), fromPath: z.string().max(500).default("/") });

export const assistantFeedbackBody = z.object({ helpful: z.boolean(), reason: z.string().trim().max(300).optional() });

export type AssistantLinkDto = { label: string; path: string; kind: "guide" | "walkthrough" | "screen" };
export type AssistantChatSummary = { id: Id; title: string; lastMessageAt: string };
export type AssistantChatMessage = {
  id: Id;
  role: "user" | "assistant";
  text: string;
  links: AssistantLinkDto[];
  tools: { tool: string; ok: boolean; summary: string }[];
  createdAt: string;
};
export type AssistantChat = { id: Id; title: string; audience: AssistantAudience; messages: AssistantChatMessage[] };
export type AssistantStatusDto = {
  available: boolean;
  reason: null | "off" | "org_off" | "budget" | "platform_budget" | "daily_limit" | "no_ai";
  /** The plain sentence the panel shows when not available. */
  message: string | null;
  questionsLeftToday: number;
};
export type AssistantHandover = { contactPath: string; chatId: Id };

/** Server-sent events of POST /api/assistant/messages. */
export type AssistantStreamEvent =
  | { event: "chat"; data: { id: Id } }
  | { event: "delta"; data: { text: string } }
  | { event: "tool"; data: { name: string; label: string } }
  | { event: "done"; data: { messageId: Id; links: AssistantLinkDto[]; answered: boolean } }
  | { event: "error"; data: { message: string } };
